import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, Image, ActivityIndicator, ScrollView, Modal } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { readAndCompressImage } from 'browser-image-resizer';
import { uploadBase64, getFileURL } from "../db_calls";
import { useCustomer, useCheckInStore, useAuthStore, useUsersStore } from "../stores";
import { isAdminBadgeLevel, ADMIN_BADGE_AUTH_LEVEL } from "../utils/pinConfig";
import { createChangeTrackingEntry } from "../utils/helperFuns";
import uploadQueue from "../uploadQueue";
import uploadProcessor from "../uploadProcessor";
import { db, storage } from "../firebase.config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getTenantStoreContext } from "../services/userStoreCache";
import { getCollectionPath } from "../utils/dbPaths";
import { colors, radius, spacing, elevation } from "../theme";
import FloatingBackButton from "../components/FloatingBackButton";

const Photo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get media category and bookingId from navigation state
  const category = location.state?.category; // 'outbound', 'checkIn', or 'messages'
  const bookingId = location.state?.bookingId;
  
  // Get route information
  const fromCheckIn = location.state?.fromCheckIn || false;
  const fromToday = location.state?.fromToday || false;
  const fromBookings = location.state?.fromBookings || false;
  const fromMessages = location.state?.fromMessages || false;
  const fromProblemReport = location.state?.fromProblemReport || false;
  const returnTo = location.state?.returnTo;
  
  // Check if coming from check-in page (legacy support)
  const checkInContext = fromCheckIn 
    ? {
        bookingId: location.state.bookingId,
        productId: location.state.productId,
        productType: location.state.productType,
      }
    : null;
  
  // Determine effective category based on route
  const effectiveCategory = category || (fromCheckIn ? 'checkIn' : fromToday || fromBookings ? 'outbound' : 'messages');
  
  console.log('📸 Photo page loaded with category:', effectiveCategory, 'bookingId:', bookingId, 'route:', { fromCheckIn, fromToday, fromBookings, fromMessages });
  
  const [photos, setPhotos] = useState([]); // Photos stored in memory (not auto-uploaded)
  // Detect mobile on initialization (not in useEffect)
  const [isMobile, setIsMobile] = useState(() => {
    const checkMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    console.log("🔵 [INIT] Mobile detection", JSON.stringify({ isMobile: checkMobile, userAgent: navigator.userAgent }));
    return checkMobile;
  });
  const [uploadedCount, setUploadedCount] = useState(0);
  const [showWebcam, setShowWebcam] = useState(false);
  const [stream, setStream] = useState(null);
  const streamRef = useRef(null); // Keep ref to stream for cleanup
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueInitialized, setQueueInitialized] = useState(false);
  const [uploadedPhotoUrls, setUploadedPhotoUrls] = useState([]);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [currentFacingMode, setCurrentFacingMode] = useState("environment"); // 'environment' or 'user'
  const [uploadingPhotos, setUploadingPhotos] = useState(false); // Track batch upload status
  const [photoMessage, setPhotoMessage] = useState(""); // Message/caption for photos
  const [isRecording, setIsRecording] = useState(false); // Video recording state
  const [recordedVideo, setRecordedVideo] = useState(null); // Recorded video blob/data
  const [recordingTime, setRecordingTime] = useState(0); // Recording duration in seconds
  const [uploadingVideo, setUploadingVideo] = useState(false); // Track video upload status
  const [completingWithoutPhotos, setCompletingWithoutPhotos] = useState(false); // Admin check-out override in flight
  const [overrideArmed, setOverrideArmed] = useState(false); // Admin unlocked Save with no media
  // Media that has already landed on the booking this visit. Saving empties
  // `photos`, so without this the pictures just vanish and there is nothing on
  // screen saying the upload worked.
  const [savedMedia, setSavedMedia] = useState([]);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const isOpeningCamera = useRef(false);
  const mediaRecorderRef = useRef(null); // MediaRecorder instance
  const recordingTimerRef = useRef(null); // Timer for recording duration

  // Get current customer from Zustand store
  const currentCustomer = useCustomer((state) => state.customer);

  // Gate for the check-out override. Auth level on the signed-in staff record,
  // NOT the Firebase `admin` claim — every staff member shares one Firebase
  // account, so that claim is identical for everyone and cannot tell roles apart.
  // The live users record wins over selectedUser, which is a localStorage
  // snapshot whose authLevel goes stale after a promotion.
  const selectedStaff = useAuthStore((state) => state.selectedUser);
  const allUsers = useUsersStore((state) => state.users);
  const liveStaff = (selectedStaff?.id && allUsers?.[selectedStaff.id]) || selectedStaff;
  const isOverrideEligible = isAdminBadgeLevel(liveStaff?.authLevel);

  // Get check-in store functions
  const updateBookingIntake = useCheckInStore((state) => state.updateBookingIntake);

  // Helper function to check if there are photos (non-video items)
  const hasPhotos = () => {
    return photos.some(item => !(item.type === 'video' || item.name?.includes('.webm')));
  };

  // Helper function to check if there is a video
  const hasVideo = () => {
    return recordedVideo !== null || photos.some(item => item.type === 'video' || item.name?.includes('.webm'));
  };

  // Cleanup function to clear unsent photos/videos
  const clearUnsentMedia = () => {
    if (photos.length > 0 || recordedVideo) {
      console.log("🧹 Clearing unsent photos/videos on unmount/navigation");
      setPhotos([]);
      setRecordedVideo(null);
      setRecordingTime(0);
      setPhotoMessage("");
      setUploadedPhotoUrls([]);
      setUploadedCount(0);
    }
  };

  // Initialize queue on-demand (when first photo is taken) instead of on mount
  const ensureQueueInitialized = async () => {
    if (queueInitialized) return;
    try {
      console.log("🔵 [ensureQueueInitialized] Initializing upload queue on-demand...");
        await uploadQueue.init();
        uploadProcessor.init();
        setQueueInitialized(true);
        console.log("✅ Upload queue system initialized");
      } catch (error) {
      console.error("❌ Failed to initialize upload queue:", JSON.stringify({ error: error.message, stack: error.stack }));
    }
  };

  React.useEffect(() => {
    console.log("🔵 [useEffect-MOUNT] Photo component mounting", JSON.stringify({
      timestamp: new Date().toISOString(),
      streamRef: streamRef.current ? "exists" : "null",
      videoRef: videoRef.current ? "exists" : "null",
      showWebcam,
      stream: stream ? "exists" : "null",
      isOpeningCamera: isOpeningCamera.current,
    }));

    // Cleanup on page unload only - don't interfere with visibility changes
    const handleBeforeUnload = () => {
      console.log("🔵 [useEffect-BEFOREUNLOAD] Page unloading - cleaning up camera", JSON.stringify({
        timestamp: new Date().toISOString(),
        streamRef: streamRef.current ? "exists" : "null",
        videoRef: videoRef.current ? "exists" : "null",
        videoSrcObject: videoRef.current?.srcObject ? "exists" : "null",
      }));
      // Clear unsent media on page unload
      clearUnsentMedia();
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        console.log("🔵 [useEffect-BEFOREUNLOAD] Stopping tracks from streamRef", JSON.stringify({ trackCount: tracks.length }));
        tracks.forEach((track) => {
          console.log("🔵 [useEffect-BEFOREUNLOAD] Stopping track", JSON.stringify({ kind: track.kind, readyState: track.readyState, id: track.id }));
          track.stop();
        });
        streamRef.current = null;
      }
      if (videoRef.current && videoRef.current.srcObject) {
        const videoStream = videoRef.current.srcObject;
        if (videoStream instanceof MediaStream) {
          const tracks = videoStream.getTracks();
          console.log("🔵 [useEffect-BEFOREUNLOAD] Stopping tracks from video element", JSON.stringify({ trackCount: tracks.length }));
          tracks.forEach((track) => {
            console.log("🔵 [useEffect-BEFOREUNLOAD] Stopping video track", JSON.stringify({ kind: track.kind, readyState: track.readyState, id: track.id }));
            track.stop();
          });
        }
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

      // Cleanup - this is the ONLY place camera cleanup should happen on unmount
      return () => {
        console.log("🔵 [useEffect-UNMOUNT] Photo component unmounting (main effect)", JSON.stringify({
          timestamp: new Date().toISOString(),
          streamRef: streamRef.current ? "exists" : "null",
          videoRef: videoRef.current ? "exists" : "null",
          videoSrcObject: videoRef.current?.srcObject ? "exists" : "null",
          showWebcam,
          stream: stream ? "exists" : "null",
        }));
        
        // Clear unsent media on unmount
        clearUnsentMedia();
        
        // Cleanup camera (ONLY in main unmount to avoid conflicts)
        if (streamRef.current) {
          const tracks = streamRef.current.getTracks();
          console.log("🔵 [useEffect-UNMOUNT] Stopping camera stream", JSON.stringify({ trackCount: tracks.length }));
          tracks.forEach((track) => {
            console.log("🔵 [useEffect-UNMOUNT] Stopping track", JSON.stringify({ kind: track.kind, readyState: track.readyState, id: track.id }));
            track.stop();
          });
          streamRef.current = null;
        }
        if (videoRef.current && videoRef.current.srcObject) {
          const videoStream = videoRef.current.srcObject;
          if (videoStream instanceof MediaStream) {
            const tracks = videoStream.getTracks();
            console.log("🔵 [useEffect-UNMOUNT] Stopping video element tracks", JSON.stringify({ trackCount: tracks.length }));
            tracks.forEach((track) => {
              console.log("🔵 [useEffect-UNMOUNT] Stopping video track", JSON.stringify({ kind: track.kind, readyState: track.readyState, id: track.id }));
              track.stop();
            });
          }
          videoRef.current.pause();
          videoRef.current.srcObject = null;
          console.log("🔵 [useEffect-UNMOUNT] Video element cleared");
        }
      
              // Stop video recording if active
              if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
                console.log("🔵 [useEffect-UNMOUNT] Stopped active video recording");
              }
              if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
              }

              // Clear photos on unmount
      setPhotos([]);
      setUploadedCount(0);
              setRecordedVideo(null);
              setRecordingTime(0);
              console.log("🔵 [useEffect-UNMOUNT] Photos and video cleared");
              
              uploadProcessor.stop();
              window.removeEventListener('beforeunload', handleBeforeUnload);
              console.log("🔵 [useEffect-UNMOUNT] Cleanup complete");
    };
  }, []);

  // Listen to upload processor events
  React.useEffect(() => {
    console.log("🔵 [useEffect-UPLOAD-PROCESSOR] Setting up upload processor listener", JSON.stringify({ queueInitialized }));
    const handleUploadEvent = (event) => {
      console.log("🔵 [useEffect-UPLOAD-PROCESSOR] Upload event received", JSON.stringify({
        timestamp: new Date().toISOString(),
        eventType: event.type,
        event: event,
      }));

      if (event.type === "network") {
        setIsOnline(event.online);
      } else if (event.type === "upload_success") {
        // Update photo status to uploaded
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === event.photoId
              ? {
                  ...p,
                  status: "uploaded",
                  queueId: null,
                  downloadUrl: event.downloadUrl,
                }
              : p
          )
        );
        setUploadedCount((prev) => prev + 1);

        // Track uploaded photo URL for MMS sending
        if (event.downloadUrl) {
          setUploadedPhotoUrls((prev) => [...prev, event.downloadUrl]);
          console.log(`📎 Photo URL tracked for MMS: ${event.downloadUrl}`);
        }

        // Clear photo data from memory after showing success briefly (2 seconds)
        setTimeout(() => {
          setPhotos((prev) => {
            const filtered = prev.filter((p) => p.id !== event.photoId);
            console.log(
              `🗑️ Cleared photo ${event.photoId} from memory (${filtered.length} remaining)`
            );
            return filtered;
          });
        }, 2000);
      } else if (event.type === "upload_failed") {
        // Update photo status to failed
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === event.photoId
              ? { ...p, status: "failed", error: event.error }
              : p
          )
        );
      } else if (event.type === "upload_started") {
        // Update photo status to uploading
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === event.photoId ? { ...p, status: "uploading" } : p
          )
        );
      } else if (event.type === "upload_requeued") {
        // Update photo status back to queued
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === event.photoId ? { ...p, status: "queued" } : p
          )
        );
      }
    };

      uploadProcessor.addListener(handleUploadEvent);

    return () => {
      console.log("🔵 [useEffect-UPLOAD-PROCESSOR] Cleanup - removing upload processor listener", JSON.stringify({
        timestamp: new Date().toISOString(),
        queueInitialized,
      }));
      uploadProcessor.removeListener(handleUploadEvent);
    };
  }, [queueInitialized]);

  const handleTakePhoto = () => {
    console.log("🔵 [handleTakePhoto] Called", JSON.stringify({
      timestamp: new Date().toISOString(),
      currentCustomer: currentCustomer ? currentCustomer.fullName : "null",
      showWebcam,
      stream: stream ? "exists" : "null",
      streamRef: streamRef.current ? "exists" : "null",
      isOpeningCamera: isOpeningCamera.current,
      videoRef: videoRef.current ? "exists" : "null",
    }));
    // Use webcam for both mobile and desktop
    openWebcam();
  };

  // Simple cleanup function
  const cleanupCamera = (closeWebcam = false) => {
    console.log("🔵 [cleanupCamera] Called", JSON.stringify({
      timestamp: new Date().toISOString(),
      closeWebcam,
      stream: stream ? "exists" : "null",
      streamRef: streamRef.current ? "exists" : "null",
      videoRef: videoRef.current ? "exists" : "null",
      videoSrcObject: videoRef.current?.srcObject ? "exists" : "null",
      showWebcam,
    }));

    const allTracks = [];

    // Collect tracks from state stream
    if (stream) {
      const tracks = stream.getTracks();
      console.log("🔵 [cleanupCamera] Found tracks in state stream", JSON.stringify({ trackCount: tracks.length }));
      allTracks.push(...tracks);
    }

    // Collect tracks from ref
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      console.log("🔵 [cleanupCamera] Found tracks in streamRef", JSON.stringify({ trackCount: tracks.length }));
      allTracks.push(...tracks);
    }

    // Collect tracks from video element
    if (videoRef.current?.srcObject instanceof MediaStream) {
      const tracks = videoRef.current.srcObject.getTracks();
      console.log("🔵 [cleanupCamera] Found tracks in video element", JSON.stringify({ trackCount: tracks.length }));
      allTracks.push(...tracks);
    }

    // Stop all tracks (deduplicate by id to avoid stopping same track twice)
    const uniqueTracks = Array.from(new Map(allTracks.map(track => [track.id, track])).values());
    console.log("🔵 [cleanupCamera] Stopping unique tracks", JSON.stringify({ totalTracks: uniqueTracks.length }));
    
    uniqueTracks.forEach((track) => {
      console.log("🔵 [cleanupCamera] Stopping track", JSON.stringify({ 
        kind: track.kind, 
        readyState: track.readyState, 
        id: track.id,
        label: track.label 
      }));
      if (track.readyState !== 'ended') {
        track.stop();
        // Verify it stopped
        setTimeout(() => {
          console.log("🔵 [cleanupCamera] Track state after stop", JSON.stringify({ 
            id: track.id, 
            readyState: track.readyState 
          }));
        }, 100);
      }
    });

    // Clear refs
    if (streamRef.current) {
      streamRef.current = null;
      console.log("🔵 [cleanupCamera] streamRef.current set to null");
    }

    // Clear video element
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      console.log("🔵 [cleanupCamera] Video element paused and srcObject cleared");
    }

    setStream(null);
    if (closeWebcam) {
      console.log("🔵 [cleanupCamera] Setting showWebcam to false");
      setShowWebcam(false);
    }
    console.log("🔵 [cleanupCamera] Cleanup complete");
  };

  const openWebcam = async (retryCount = 0) => {
    if (!currentCustomer) {
      alert("Please select a customer first");
      return;
    }

    // If camera is already open and we have a valid stream, don't do anything
    // UNLESS we are retrying, in which case we might want to force a restart
    if (showWebcam && streamRef.current && retryCount === 0) {
      return;
    }

    // If already opening, ignore (unless it's a retry)
    if (isOpeningCamera.current && retryCount === 0) {
      return;
    }

    isOpeningCamera.current = true;
    
    // Safety: Auto-reset flag after 10 seconds if it gets stuck
    const safetyTimeout = setTimeout(() => {
      isOpeningCamera.current = false;
      setCameraLoading(false);
    }, 10000);

    // Check for secure context/media device support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("Camera access is not supported in this browser or context.\n\nRequired: HTTPS (secure) connection or localhost.\n\nIf you are on mobile, please ensure you are using a modern browser and accessing the site via HTTPS.");
      setCameraLoading(false);
      isOpeningCamera.current = false;
      return;
    }

    try {
      console.log(`📷 Opening camera... (Retry: ${retryCount})`);
      setCameraLoading(true);

      // CRITICAL: Always ensure any previous tracks are stopped before requesting a new stream
      // This solves the 'NotReadableError' / 'Already in use' issue on many devices
      cleanupCamera(false);

      // Small delay to ensure browser releases the hardware lock
      if (retryCount > 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      let mediaStream;

      try {
        if (isMobile) {
          // On mobile, use facingMode directly
          const cameraConstraints = {
            video: {
              facingMode: currentFacingMode,
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
            audio: false,
          };

          // If we are retrying after a specific error, try simpler constraints
          if (retryCount > 0) {
            delete cameraConstraints.video.width;
            delete cameraConstraints.video.height;
            console.log("⚠️ Retrying mobile with simpler constraints");
          }

          mediaStream = await navigator.mediaDevices.getUserMedia(cameraConstraints);
          console.log(`✅ Mobile camera opened (${currentFacingMode})`);
        } else {
          // Desktop: use front camera
          console.log("💻 Opening camera (desktop)...");

          const desktopConstraints = {
            video: {
              facingMode: "user",
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
            audio: false,
          };

          // If we are retrying, try simpler constraints
          if (retryCount > 0) {
            console.log("⚠️ Retrying desktop with fallback constraints");
            mediaStream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: false,
            });
          } else {
            mediaStream = await navigator.mediaDevices.getUserMedia(desktopConstraints);
          }
          console.log(`✅ Desktop camera opened`);
        }
      } catch (innerError) {
        // Handle specific errors that might be fixed by a retry or fallback
        if (innerError.name === "NotReadableError" || innerError.name === "TrackStartError" || innerError.name === "OverconstrainedError") {
          if (retryCount < 2) {
            console.warn(`⚠️ Camera error (${innerError.name}), retrying...`);
            isOpeningCamera.current = false; // Reset flag for recursion
            clearTimeout(safetyTimeout);
            await openWebcam(retryCount + 1);
            return;
          }
        }
        throw innerError;
      }

      console.log("🔵 [openWebcam] MediaStream obtained", JSON.stringify({
        id: mediaStream.id,
        active: mediaStream.active,
        trackCount: mediaStream.getTracks().length,
      }));

      setStream(mediaStream);
      streamRef.current = mediaStream;
      setShowWebcam(true);

      clearTimeout(safetyTimeout); // Clear safety timeout
      isOpeningCamera.current = false; // Reset flag immediately after getting stream

      // Set video element srcObject immediately
      // We rely on the useEffect or the ref callback to handle the case where videoRef is not yet attached
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;

        // Play video
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setCameraLoading(false);
              })
              .catch((error) => {
                if (error.name !== "AbortError") {
                  console.warn("⚠️ Video play warning:", error.message);
                }
                setCameraLoading(false);
              });
          } else {
            setCameraLoading(false);
          }
      } else {
        // If video element not ready, wait briefly
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
            videoRef.current.play().catch(() => {});
          }
          setCameraLoading(false);
        }, 100);
      }
    } catch (error) {
      // ALWAYS reset flag and state, even on error
      clearTimeout(safetyTimeout); // Clear safety timeout
      setCameraLoading(false);
      setShowWebcam(false);
      isOpeningCamera.current = false;

      // Cleanup any partial stream that might have been created
      cleanupCamera(true);

      let errorMessage = "Could not access camera.";

      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        errorMessage = "Camera permission denied. Please allow camera access in your browser settings.";
      } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        errorMessage = "No camera device found.";
      } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
        errorMessage = "Camera is already in use by another application or tab.";
      } else if (error.name === "OverconstrainedError") {
        errorMessage = "Camera does not match required constraints.";
      }

      alert(`${errorMessage}\n\nTechnical details: ${error.name}: ${error.message}`);
    }
  };

  const captureFromWebcam = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    // Don't allow photos if video exists
    if (hasVideo()) {
      alert("Cannot take photos when a video is ready. Please clear the video first.");
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to base64
    const photoData = canvas.toDataURL("image/jpeg", 0.9);

    console.log("📸 Photo captured from webcam");

    const photoId = Date.now();
    const newPhoto = {
      id: photoId,
      data: photoData,
      name: "webcam_photo.jpg",
      status: "pending", // Store in memory, not auto-uploaded
    };

    setPhotos((prev) => [...prev, newPhoto]);
    console.log("📸 Photo stored in memory. Total photos:", photos.length + 1);

    // Don't close webcam - allow multiple captures
  };

  const startVideoRecording = () => {
    if (!streamRef.current || !videoRef.current) {
      console.warn("⚠️ Cannot start recording: No camera stream available");
      return;
    }
    
    // Don't allow video if photos exist
    if (hasPhotos()) {
      alert("Cannot record video when photos are ready. Please clear the photos first.");
      return;
    }

    try {
      const stream = streamRef.current;
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8,opus',
        videoBitsPerSecond: 2500000, // 2.5 Mbps for good quality
      });

      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        console.log("🎥 Video recording stopped, size:", blob.size, "bytes");
        
        // Convert blob to base64 for storage
        const reader = new FileReader();
        reader.onloadend = () => {
          const videoData = reader.result; // Base64 data URL
          const videoId = Date.now();
          const videoObject = {
            id: videoId,
            data: videoData,
            blob: blob,
            name: `video_${videoId}.webm`,
            duration: recordingTime,
            type: 'video', // Mark as video
          };
          setRecordedVideo(videoObject);
          
          // Add video to photos array so it appears in the grid
          setPhotos((prev) => [...prev, videoObject]);
          console.log("🎥 Video stored in memory and added to photos grid");

          // Auto-close camera widget after recording stops
          cleanupCamera(true);
        };
        reader.onerror = (error) => {
          console.error("❌ Error reading video blob:", error);
          alert("Failed to process video recording");
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorder.onerror = (error) => {
        console.error("❌ MediaRecorder error:", error);
        alert("Error recording video: " + error.message);
        setIsRecording(false);
        setRecordingTime(0);
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          // Auto-stop at 30 seconds
          if (newTime >= 30) {
            stopVideoRecording();
            return 30;
          }
          return newTime;
        });
      }, 1000);

      console.log("🎥 Video recording started");
    } catch (error) {
      console.error("❌ Failed to start video recording:", error);
      alert("Failed to start video recording: " + error.message);
    }
  };

  const stopVideoRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      console.log("🎥 Stopping video recording...");
    }

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setIsRecording(false);
    // recordingTime will be preserved until video is processed
  };

  const flipCamera = async () => {
    console.log("🔵 [flipCamera] Called", JSON.stringify({
      timestamp: new Date().toISOString(),
      currentFacingMode,
      stream: stream ? "exists" : "null",
      streamRef: streamRef.current ? "exists" : "null",
      videoRef: videoRef.current ? "exists" : "null",
      isOpeningCamera: isOpeningCamera.current,
    }));

    // Stop current stream tracks before flipping (minimal cleanup, not full cleanupCamera)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        if (track.readyState !== 'ended') {
          track.stop();
        }
      });
    }
    if (videoRef.current?.srcObject instanceof MediaStream) {
      videoRef.current.srcObject.getTracks().forEach(track => {
        if (track.readyState !== 'ended') {
          track.stop();
        }
      });
      videoRef.current.srcObject = null;
    }

    // Toggle facing mode
    const newFacingMode =
      currentFacingMode === "environment" ? "user" : "environment";
    setCurrentFacingMode(newFacingMode);
    console.log("🔵 [flipCamera] Facing mode changed", JSON.stringify({ from: currentFacingMode, to: newFacingMode }));

    // Wait a bit longer before reopening
    console.log("🔵 [flipCamera] Waiting 300ms before reopening...");
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log("🔵 [flipCamera] Wait complete, reopening camera");

    // Reopen camera with new facing mode
      openWebcam();
  };

  const closeWebcam = async () => {
    console.log("🔵 [closeWebcam] Called", JSON.stringify({
      timestamp: new Date().toISOString(),
      currentFacingMode,
      stream: stream ? "exists" : "null",
      streamRef: streamRef.current ? "exists" : "null",
      videoRef: videoRef.current ? "exists" : "null",
      videoSrcObject: videoRef.current?.srcObject ? "exists" : "null",
      isOpeningCamera: isOpeningCamera.current,
      showWebcam,
    }));

    cleanupCamera(true); // Pass true to close webcam
    isOpeningCamera.current = false; // Reset flag when closing
    setCurrentFacingMode("environment"); // Reset to rear camera for next time

    console.log("🔵 [closeWebcam] Webcam closed and stream released", JSON.stringify({
      isOpeningCamera: false,
      currentFacingMode: "environment",
    }));
    // Photos are now stored in memory, not auto-sent
  };

  // Handle saving media for check-in
  const handleSaveForCheckIn = async () => {
    if (!checkInContext || photos.length === 0) return false;

    setUploadingPhotos(true);
    try {
      const { bookingId, productId, productType } = checkInContext;
      const messageText = photoMessage.trim() || null;
      
      // Separate photos from videos
      const photoItems = photos.filter((item) => !(item.type === 'video' || item.name?.includes('.webm')));
      const videoItems = photos.filter((item) => item.type === 'video' || item.name?.includes('.webm'));

      const { tenantId, storeId } = getTenantStoreContext();
      const messages = [];
      const now = new Date();

      // Handle text message first (if provided)
      if (messageText) {
        messages.push({
          type: "text",
          text: messageText,
          createdAt: now.toISOString(),
        });
      }

      // Handle videos
      if (videoItems.length > 0) {
        const video = videoItems[0];
        let videoBase64 = video.data;
        if (!videoBase64 && video.blob) {
          videoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(video.blob);
          });
        }

        // Upload video to storage - NEW UNIFIED PATH
        const timestamp = Date.now();
        const videoPath = `bookings/${bookingId}/media/${effectiveCategory}/${timestamp}.webm`;
        const videoStorageRef = ref(storage, videoPath);
        
        // Convert base64 to Blob
        const videoBlob = await (await fetch(videoBase64)).blob();
        await uploadBytes(videoStorageRef, videoBlob);
        const videoURL = await getDownloadURL(videoStorageRef);

        // Create video message
        messages.push({
          type: "video",
          videoURL: videoURL,
          text: messageText || null,
          createdAt: now.toISOString(),
        });
      }

      // Handle photos
      if (photoItems.length > 0) {
        for (const photo of photoItems) {
          try {
            // Convert base64 to Blob and compress
            let imageData = photo.data;
            if (typeof photo.data === 'string' && photo.data.startsWith('data:')) {
              const response = await fetch(photo.data);
              imageData = await response.blob();
            }

            const resizedBlob = await readAndCompressImage(imageData, {
              quality: 0.8,
              maxWidth: 1920,
              maxHeight: 1920,
            });

            // Upload to storage - NEW UNIFIED PATH
            const timestamp = Date.now();
            const photoPath = `bookings/${bookingId}/media/${effectiveCategory}/${timestamp}.jpg`;
            const photoStorageRef = ref(storage, photoPath);
            await uploadBytes(photoStorageRef, resizedBlob);
            const photoURL = await getDownloadURL(photoStorageRef);

            // Create photo message
            messages.push({
              type: "photo",
              photoURL: photoURL,
              text: messageText || null,
              createdAt: now.toISOString(),
            });
          } catch (error) {
            console.error(`❌ Failed to process photo ${photo.id}:`, error);
          }
        }
      }

      // Extract media URLs from messages for bookingIntake.media array
      const mediaUrls = [];
      messages.forEach(msg => {
        if (msg.type === 'photo' && msg.photoURL) {
          mediaUrls.push(msg.photoURL);
        } else if (msg.type === 'video' && msg.videoURL) {
          mediaUrls.push(msg.videoURL);
        }
      });

      // Store messages and media URLs in check-in Zustand store (for UI state)
      updateBookingIntake((prev) => {
        const newIntake = { ...prev };
        if (!newIntake[productType]) newIntake[productType] = {};
        if (!newIntake[productType][productId]) {
          newIntake[productType][productId] = {
            returnedProduct: true,
            penaltyType: "percentage",
            penaltyValue: 0,
            penaltyAmount: 0,
            penaltyReason: "",
            photos: [],
            messages: [],
            media: [],
          };
        }
        
        // Add new messages to existing messages array
        newIntake[productType][productId].messages = [
          ...(newIntake[productType][productId].messages || []),
          ...messages,
        ];
        
        // Add media URLs to media array (new paradigm)
        newIntake[productType][productId].media = [
          ...(newIntake[productType][productId].media || []),
          ...mediaUrls,
        ];

        return newIntake;
      });

      // ALSO write to new media structure in Firestore booking document
      const { doc: firestoreDoc, getDoc, updateDoc, arrayUnion, Timestamp } = await import('firebase/firestore');
      
      // Try to find booking in tenant/store path first, then root
      let bookingsPath = getCollectionPath("bookings");
      let bookingRef = firestoreDoc(db, bookingsPath, bookingId);
      let bookingDoc = await getDoc(bookingRef);
      
      // If not found, try root bookings collection
      if (!bookingDoc.exists() && bookingsPath !== "bookings") {
        console.log(`⚠️ Booking not found in ${bookingsPath}, trying root bookings`);
        bookingRef = firestoreDoc(db, "bookings", bookingId);
        bookingDoc = await getDoc(bookingRef);
        if (bookingDoc.exists()) {
          bookingsPath = "bookings";
        }
      }
      
      if (!bookingDoc.exists()) {
        throw new Error(`Booking ${bookingId} not found in ${bookingsPath} or root bookings`);
      }
      
      // Get image dimensions for each photo/video (filter out text messages)
      const mediaItems = await Promise.all(
        messages
          .filter(msg => msg.type === 'photo' || msg.type === 'video') // Only process media messages
          .map(async (msg) => {
            let dimensions = null;
            if (msg.type === 'photo' && msg.photoURL) {
              try {
                // Create image element to get dimensions
                const img = new Image();
                await new Promise((resolve, reject) => {
                  img.onload = resolve;
                  img.onerror = reject;
                  img.src = msg.photoURL;
                });
                dimensions = { width: img.width, height: img.height };
              } catch (err) {
                console.warn('Could not get image dimensions:', err);
              }
            }

            const url = msg.photoURL || msg.videoURL;
            if (!url) {
              console.warn('Skipping message without URL:', msg);
              return null;
            }

            return {
              url: url,
              timestamp: msg.createdAt,
              type: msg.type === 'photo' ? 'image' : 'video', // Map 'photo' to 'image' for consistency
              dimensions: dimensions,
            };
          })
      );

      // Filter out any null values (messages without URLs)
      const validMediaItems = mediaItems.filter(item => item !== null);

      // Update booking document with new media items (only if there are valid items)
      if (validMediaItems.length > 0) {
        // Get user metadata for change tracking (if authenticated)
        const selectedUser = useAuthStore.getState().selectedUser;
        const userMetadata = selectedUser ? {
          userId: selectedUser.id,
          firstName: selectedUser.firstName || "",
          lastName: selectedUser.lastName || "",
        } : null;

        const updateData = {
          [`media.${effectiveCategory}`]: arrayUnion(...validMediaItems),
          updatedAt: Timestamp.now(),
        };

        // Add change tracking if user metadata is available
        if (userMetadata) {
          const changeEntry = createChangeTrackingEntry(userMetadata, {
            changeType: "photo_upload",
            fieldChanged: `media.${effectiveCategory}`,
            description: `${validMediaItems.length} media item(s) uploaded to ${effectiveCategory}`,
            timestamp: Timestamp.now(),
          });
          
          if (changeEntry) {
            updateData.editedBy = arrayUnion(changeEntry);
          }
        }

        await updateDoc(bookingRef, updateData);
      }

      // ALSO save to bookingIntake node with the new structure
      const bookingData = bookingDoc.data();
      const currentIntake = bookingData.bookingIntake || { product1: {}, product2: {} };

      // Ensure the product exists in bookingIntake
      if (!currentIntake[productType]) {
        currentIntake[productType] = {};
      }
      if (!currentIntake[productType][productId]) {
        currentIntake[productType][productId] = {
          returnedProduct: true,
          penaltyType: "percentage",
          penaltyValue: 0,
          penaltyAmount: 0,
          penaltyReason: "",
          media: [],
        };
      }

      // Add media URLs to the product's media array in bookingIntake
      const existingMedia = currentIntake[productType][productId].media || [];
      const updatedMedia = [...existingMedia, ...mediaUrls];

      // Update the product's data in bookingIntake
      currentIntake[productType][productId] = {
        ...currentIntake[productType][productId],
        media: updatedMedia,
        messages: [
          ...(currentIntake[productType][productId].messages || []),
          ...messages,
        ],
      };
      
      // Update bookingIntake with the complete structure
      // Get user metadata for change tracking (if authenticated)
      const selectedUserForIntake = useAuthStore.getState().selectedUser;
      const userMetadataForIntake = selectedUserForIntake ? {
        userId: selectedUserForIntake.id,
        firstName: selectedUserForIntake.firstName || "",
        lastName: selectedUserForIntake.lastName || "",
      } : null;

      const intakeUpdateData = {
        bookingIntake: currentIntake,
        updatedAt: Timestamp.now(),
      };

      // Add change tracking if user metadata is available
      if (userMetadataForIntake) {
        const changeEntry = createChangeTrackingEntry(userMetadataForIntake, {
          changeType: "photo_upload",
          fieldChanged: "bookingIntake",
          description: `Media uploaded to booking intake for ${productType}.${productId}`,
          timestamp: Timestamp.now(),
        });
        
        if (changeEntry) {
          intakeUpdateData.editedBy = arrayUnion(changeEntry);
        }
      }

      await updateDoc(bookingRef, intakeUpdateData);
      
      console.log(`✅ Media saved to ${bookingsPath}/${bookingId}/media.${effectiveCategory}`);
      console.log(`✅ Media URLs saved to bookingIntake.${productType}.${productId}.media`);

      // Same reason as handleSave: keep proof on screen that the upload landed.
      setSavedMedia((prev) => [
        ...prev,
        ...messages.map((msg) => ({
          url: msg.type === "video" ? msg.videoURL : msg.photoURL,
          type: msg.type === "video" ? "video" : "image",
        })).filter((item) => !!item.url),
      ]);

      // Clear media after successful save
      setPhotos([]);
      setPhotoMessage("");
      setRecordedVideo(null);
      setRecordingTime(0);

      // Close modal - no success dialog
      closeWebcam();
      return true;
    } catch (error) {
      console.error("❌ Failed to save media for check-in:", error);
      alert("Failed to save media. Please try again.");
      return false;
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Unified save function - saves media based on route
  const handleSave = async () => {
    if (photos.length === 0 && !recordedVideo) {
      // Nothing staged, nothing to save. The no-photos override path never
      // lands here — handleCompleteCheckOut routes it before calling this.
      return false;
    }

    // If from check-in, use handleSaveForCheckIn
    if (fromCheckIn && checkInContext) {
      return await handleSaveForCheckIn();
    }

    // Determine category based on route
    let saveCategory = effectiveCategory;

    if (!bookingId || !saveCategory) {
      alert("Missing booking information");
      return false;
    }

    setUploadingPhotos(true);

    try {
      const { tenantId, storeId } = getTenantStoreContext();
      const photoItems = photos.filter((item) => !(item.type === 'video' || item.name?.includes('.webm')));
      const videoItems = photos.filter((item) => item.type === 'video' || item.name?.includes('.webm'));
      const now = new Date();
      const mediaItems = [];

      // Handle videos
      if (recordedVideo || videoItems.length > 0) {
        const video = recordedVideo || videoItems[0];
        let videoBase64 = video.data;
        if (!videoBase64 && video.blob) {
          videoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(video.blob);
          });
        }

        // Upload video to storage
        const timestamp = Date.now();
        const videoPath = `bookings/${bookingId}/media/${saveCategory}/${timestamp}.webm`;
        const videoStorageRef = ref(storage, videoPath);
        
        const videoBlob = await (await fetch(videoBase64)).blob();
        await uploadBytes(videoStorageRef, videoBlob);
        const videoURL = await getDownloadURL(videoStorageRef);

        mediaItems.push({
          url: videoURL,
          timestamp: now.toISOString(),
          type: "video",
          dimensions: null,
        });
      }

      // Handle photos
      for (const photo of photoItems) {
        try {
          let imageData = photo.data;
          if (typeof photo.data === 'string' && photo.data.startsWith('data:')) {
            const response = await fetch(photo.data);
            imageData = await response.blob();
          }

          const resizedBlob = await readAndCompressImage(imageData, {
            quality: 0.8,
            maxWidth: 1920,
            maxHeight: 1920,
          });

          // Upload to storage
          const timestamp = Date.now();
          const photoPath = `bookings/${bookingId}/media/${saveCategory}/${timestamp}.jpg`;
          const photoStorageRef = ref(storage, photoPath);
          await uploadBytes(photoStorageRef, resizedBlob);
          const photoURL = await getDownloadURL(photoStorageRef);

          // Get dimensions
          let dimensions = null;
          try {
            const img = new Image();
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = photoURL;
            });
            dimensions = { width: img.width, height: img.height };
          } catch (err) {
            console.warn('Could not get image dimensions:', err);
          }

          mediaItems.push({
            url: photoURL,
            timestamp: now.toISOString(),
            type: "image",
            dimensions: dimensions,
          });
        } catch (error) {
          console.error(`❌ Failed to process photo ${photo.id}:`, error);
        }
      }

      // Write to Firestore booking document - find correct path
      const { doc: firestoreDoc, getDoc, updateDoc, arrayUnion } = await import('firebase/firestore');
      
      // Try to find booking in tenant/store path first, then root
      let bookingsPath = getCollectionPath("bookings");
      let bookingRef = firestoreDoc(db, bookingsPath, bookingId);
      let bookingDoc = await getDoc(bookingRef);
      
      // If not found, try root bookings collection
      if (!bookingDoc.exists() && bookingsPath !== "bookings") {
        console.log(`⚠️ Booking not found in ${bookingsPath}, trying root bookings`);
        bookingRef = firestoreDoc(db, "bookings", bookingId);
        bookingDoc = await getDoc(bookingRef);
        if (bookingDoc.exists()) {
          bookingsPath = "bookings";
        }
      }
      
      if (!bookingDoc.exists()) {
        throw new Error(`Booking ${bookingId} not found in ${bookingsPath} or root bookings`);
      }
      
      await updateDoc(bookingRef, {
        [`media.${saveCategory}`]: arrayUnion(...mediaItems)
      });
      
      console.log(`✅ Media saved to ${bookingsPath}/${bookingId}/media.${saveCategory}`);

      // Keep what just landed on the booking on screen. Clearing `photos` below
      // is what used to make an upload look like it did nothing.
      setSavedMedia((prev) => [...prev, ...mediaItems]);

      // Clear media after successful save
      setPhotos([]);
      setPhotoMessage("");
      setRecordedVideo(null);
      setRecordingTime(0);

      // No alert - just clear and continue
      return true;
    } catch (error) {
      console.error("❌ Failed to save media:", error);
      alert("Failed to save media. Please try again.");
      return false;
    } finally {
      setUploadingPhotos(false);
    }
  };

  const hasStagedMedia = photos.length > 0 || recordedVideo !== null;

  // The Check Out flow from a booking. Here Save is not "save and keep going" --
  // it is the last step of checking the equipment out, so it gets its own button
  // that finishes and returns to the booking.
  const isCheckOut = effectiveCategory === "outbound" && !!bookingId && !fromCheckIn;

  // Admin override for the Check Out step. Save is hard-disabled at zero media, so
  // normally a check-out cannot be finished without shooting pictures. Level
  // ADMIN_BADGE_AUTH_LEVEL+ staff can arm this to unlock Save and close the
  // check-out empty (camera failed, customer already gone, etc.). Only offered
  // when there is genuinely nothing staged — with photos in hand, Save works.
  const canOverrideCheckOut =
    isOverrideEligible &&
    effectiveCategory === "outbound" &&
    !!bookingId &&
    !hasStagedMedia &&
    !uploadingPhotos;

  // Arming only unlocks the button; Complete Check-Out still performs it.
  const completeUnlockedByOverride = canOverrideCheckOut && overrideArmed;

  // Disarm as soon as the override stops applying, so it can't leak into a later
  // state where Save would mean something different.
  useEffect(() => {
    if (!canOverrideCheckOut && overrideArmed) setOverrideArmed(false);
  }, [canOverrideCheckOut, overrideArmed]);

  const handleCompleteWithoutPhotos = async () => {
    if (!completeUnlockedByOverride || completingWithoutPhotos) return;

    setCompletingWithoutPhotos(true);
    try {
      const { doc: firestoreDoc, getDoc, updateDoc, arrayUnion, Timestamp } =
        await import("firebase/firestore");

      // Same two-step lookup handleSave uses: bookings live under the tenant/store
      // path on newer stores and at the root on older ones.
      let bookingsPath = getCollectionPath("bookings");
      let bookingRef = firestoreDoc(db, bookingsPath, bookingId);
      let bookingDoc = await getDoc(bookingRef);

      if (!bookingDoc.exists() && bookingsPath !== "bookings") {
        console.log(`⚠️ Booking not found in ${bookingsPath}, trying root bookings`);
        bookingRef = firestoreDoc(db, "bookings", bookingId);
        bookingDoc = await getDoc(bookingRef);
        if (bookingDoc.exists()) {
          bookingsPath = "bookings";
        }
      }

      if (!bookingDoc.exists()) {
        throw new Error(`Booking ${bookingId} not found in ${bookingsPath} or root bookings`);
      }

      const updateData = { updatedAt: Timestamp.now() };

      // Nothing lands in media.outbound, so this editedBy entry is the only record
      // that the check-out happened at all — without it an overridden check-out is
      // indistinguishable from one that was never done.
      const selectedUser = useAuthStore.getState().selectedUser;
      const userMetadata = selectedUser
        ? {
            userId: selectedUser.id,
            firstName: selectedUser.firstName || "",
            lastName: selectedUser.lastName || "",
          }
        : null;

      if (userMetadata) {
        const changeEntry = createChangeTrackingEntry(userMetadata, {
          changeType: "check_out_override",
          fieldChanged: "media.outbound",
          description: "Check-out completed without photos (admin override)",
          timestamp: Timestamp.now(),
        });

        if (changeEntry) {
          updateData.editedBy = arrayUnion(changeEntry);
        }
      }

      await updateDoc(bookingRef, updateData);
      console.log(`✅ Check-out completed without photos: ${bookingsPath}/${bookingId}`);

      navigate(-1);
    } catch (error) {
      console.error("❌ Failed to complete check-out without photos:", error);
      alert("Failed to complete check-out. Please try again.");
    } finally {
      setCompletingWithoutPhotos(false);
    }
  };

  // Finishes the check-out: pushes whatever is staged onto the booking, then
  // goes back. Anything already saved this visit counts, so a second trip
  // through the camera can still be closed out with nothing staged.
  const handleCompleteCheckOut = async () => {
    if (uploadingPhotos || completingWithoutPhotos) return;

    if (!hasStagedMedia) {
      if (completeUnlockedByOverride) {
        await handleCompleteWithoutPhotos(); // navigates back itself
        return;
      }
      if (savedMedia.length > 0) {
        navigate(-1);
      }
      return;
    }

    const saved = await handleSave();
    if (saved) {
      navigate(-1);
    }
  };

  const handleRemovePhoto = (photoId) => {
    setPhotos((prev) => {
      const filtered = prev.filter((photo) => photo.id !== photoId);
      // If removed item was the recorded video, clear video state and default message
      const removedItem = prev.find((photo) => photo.id === photoId);
      if (removedItem && (removedItem.type === 'video' || removedItem.name?.includes('.webm'))) {
        setRecordedVideo(null);
        setRecordingTime(0);
        setPhotoMessage(""); // Clear message when video is removed
      }
      return filtered;
    });
    console.log(`🗑️ Photo/video ${photoId} removed from memory`);
  };

  const queuePhotoForUpload = async (photoData, photoId) => {
    if (!currentCustomer || !currentCustomer.phone) {
      console.error("❌ Cannot queue photo - no customer selected");
      throw new Error("No customer selected");
    }

    const customerPhone = currentCustomer.phone;

    console.log(
      `📥 Queueing photo ${photoId} for upload to customer ${customerPhone}`
    );
    console.log(`   Photo data length: ${photoData.length} bytes`);
    console.log(`   Queue initialized: ${queueInitialized}`);

    try {
      // Initialize queue on-demand if not already initialized
      await ensureQueueInitialized();

      // Add to upload queue
      const queueId = await uploadQueue.addToQueue(
        photoData,
        customerPhone,
        photoId
      );

      console.log(
        `✅ Photo ${photoId} queued successfully (Queue ID: ${queueId})`
      );

      // If online, trigger immediate processing
      if (navigator.onLine) {
        console.log("🌐 Online - triggering immediate upload processing");
        await uploadProcessor.processQueue();
      } else {
        console.log("📴 Offline - photo will upload when network returns");
      }

      return { queueId, status: navigator.onLine ? "uploading" : "queued" };
    } catch (error) {
      console.error("❌ Failed to queue photo:", error);
      console.error("   Error details:", error.message, error.stack);
      throw error;
    }
  };

  const handlePhotoCapture = (event) => {
    try {
      console.log("📸 handlePhotoCapture called");
      const file = event.target.files?.[0];

      if (!file) {
        console.log("No file selected");
        return;
      }

      console.log("File selected:", file.name, "Size:", file.size);

      // Check if customer is selected (only for MMS, not for check-in)
      if (!checkInContext && (!currentCustomer || !currentCustomer.phone)) {
        console.warn("No customer selected");
        alert("Please select a customer before taking photos");
        event.target.value = null;
        return;
      }

      console.log("Customer selected:", currentCustomer.fullName);

      const reader = new FileReader();

      reader.onloadend = () => {
        try {
          console.log("FileReader onloadend triggered");

          if (!reader.result) {
            console.error("FileReader result is empty");
            return;
          }

          const photoId = Date.now();
          const newPhoto = {
            id: photoId,
            data: reader.result,
            name: file.name,
            status: "pending", // Store in memory, not auto-uploaded
          };

          console.log("Adding photo to state, ID:", newPhoto.id);
          setPhotos((prev) => {
            const updated = [...prev, newPhoto];
            console.log("Photos state updated, count:", updated.length);
            return updated;
            });
        } catch (error) {
          console.error("❌ Error in reader.onloadend:", error);
          alert("Error processing photo: " + error.message);
        }
      };

      reader.onerror = (error) => {
        console.error("❌ FileReader error:", error);
        alert("Failed to read photo file");
      };

      console.log("Starting to read file...");
      reader.readAsDataURL(file);

      // Reset file input
      event.target.value = null;
      console.log("File input reset");
    } catch (error) {
      console.error("❌ Error in handlePhotoCapture:", error);
      alert("Error capturing photo: " + error.message);
    }
  };

  console.log("🎬 Photo component rendering, photos count:", photos.length);

  const isDesktop = !isMobile;

  const cameraModal = (
    <>
      {/* Camera Modal */}
      {showWebcam && (
        <View style={styles.webcamOverlay}>
          <View
            style={[
              styles.webcamContainer,
              isMobile && styles.webcamContainerMobile,
            ]}
          >
            <View
              style={[
                styles.videoContainer,
                isMobile && styles.videoContainerMobile,
              ]}
            >
              {/* Camera Loading Overlay */}
              {cameraLoading && (
                <View style={styles.cameraLoadingOverlay}>
                  <ActivityIndicator size="large" color="#FFFFFF" />
                  <Text style={styles.cameraLoadingText}>
                    Starting camera...
                  </Text>
                </View>
              )}

              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  backgroundColor: "#0F1A19",
                }}
              />
            </View>

            <View style={styles.webcamActions}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.closeButton, isRecording && styles.buttonDisabled]}
                onPress={closeWebcam}
                disabled={isRecording}
              >
                <Text style={[
                  styles.closeButtonIcon,
                  photos.length > 0 ? styles.closeButtonIconGreen : styles.closeButtonIconRed,
                  isRecording && styles.buttonIconDisabled
                ]}>
                  {photos.length > 0 ? '✓' : '✕'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.captureButton, (isRecording || hasVideo()) && styles.buttonDisabled]}
                onPress={captureFromWebcam}
                disabled={isRecording || hasVideo()}
              >
                <Text style={[
                  styles.captureButtonIcon,
                  (isRecording || hasVideo()) && styles.buttonIconDisabled
                ]}>📸</Text>
              </TouchableOpacity>

              {!isRecording ? (
              <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.recordVideoButton, hasPhotos() && styles.buttonDisabled]}
                  onPress={startVideoRecording}
                  disabled={hasPhotos()}
                >
                  <Text style={[
                    styles.recordVideoButtonIcon,
                    hasPhotos() && styles.buttonIconDisabled
                  ]}>🔴</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.stopRecordingButton}
                  onPress={stopVideoRecording}
                >
                  <Text style={styles.stopRecordingButtonIcon}>⏹</Text>
              </TouchableOpacity>
              )}

              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.flipButton, isRecording && styles.buttonDisabled]}
                onPress={flipCamera}
                disabled={isRecording}
              >
                <Text style={[
                  styles.flipButtonIcon,
                  isRecording && styles.buttonIconDisabled
                ]}>🔄</Text>
              </TouchableOpacity>
            </View>

            {/* Thumbnails of what has been shot so far. The camera stays open for
                multiple shots, so without this there is no proof a picture was
                actually captured until the camera is closed. */}
            {photos.length > 0 && (
              <View style={styles.cameraFilmstrip}>
                <Text style={styles.cameraFilmstripLabel}>
                  {photos.length} ready
                </Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={styles.cameraFilmstripRow}>
                    {photos.map((item) => {
                      const isVideo =
                        item.type === "video" ||
                        item.name?.includes(".webm") ||
                        item.name?.includes("video_");
                      return (
                        <View key={item.id} style={styles.cameraFilmstripItem}>
                          {isVideo ? (
                            <video
                              src={item.data}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              src={item.data}
                              alt=""
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          )}
                          <TouchableOpacity
                            style={styles.filmstripRemoveButton}
                            onPress={() => handleRemovePhoto(item.id)}
                          >
                            <Text style={styles.filmstripRemoveText}>✕</Text>
                          </TouchableOpacity>
                        </View>
                      );
                    })}
                  </View>
                </ScrollView>
              </View>
            )}

            {/* The old floating "N Ready" badge lived here. The filmstrip above
                says the same thing and shows the actual pictures, and the badge
                sat on top of the shutter row once the strip took up space. */}
          </View>
        </View>
      )}
    </>
  );

  // Everything that positions itself against the viewport lives here and is
  // rendered OUTSIDE the page scroller: a scroll container carries a transform,
  // and a transformed ancestor turns `position: fixed` into "fixed to that box".
  const overlays = (
    <>
      {/* Full-Screen Camera Loading Overlay */}
      {cameraLoading && !showWebcam && (
        <View style={styles.fullScreenLoadingOverlay}>
          <ActivityIndicator size="large" color="#0E7C6B" />
          <Text style={styles.fullScreenLoadingText}>Starting camera...</Text>
        </View>
      )}

      {/* Network Status Banner */}
      {!isOnline && (
        <View style={styles.offlineBanner}>
          <Text style={styles.offlineBannerText}>
            📴 Offline - Photos will upload when network returns
          </Text>
        </View>
      )}

      {/* Hidden canvas for webcam capture */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {cameraModal}

      {/* Floating Photo Counter (Bottom Right) */}
      {(uploadedCount > 0 || photos.length > 0) && (
        <View style={styles.photoCounter}>
          <Text style={styles.photoCounterNumber}>{photos.length}</Text>
          <Text style={styles.photoCounterLabel}>Photos</Text>
        </View>
      )}
    </>
  );

  const content = (
    <>
      {/* Customer indicator or Check-In indicator */}
      {checkInContext ? (
        <View style={styles.checkInBadge}>
          <Text style={styles.checkInBadgeLabel}>Check-In Photos</Text>
          <Text style={styles.checkInBadgeText}>
            Taking photos for {checkInContext.productType === "product1" ? "Product 1" : "Product 2"} item
          </Text>
        </View>
      ) : currentCustomer ? (
        <View style={styles.customerBadge}>
          <Text style={styles.customerBadgeLabel}>Customer:</Text>
          <Text style={styles.customerBadgeName}>
            {currentCustomer.fullName}
          </Text>
        </View>
      ) : (
        <View style={styles.noCustomerBadge}>
          <Text style={styles.noCustomerText}>⚠️ No customer selected</Text>
        </View>
      )}

      {/* Hidden file input for camera access (mobile only) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handlePhotoCapture}
      />



      {/* Upload Status Bar */}
      {photos.length > 0 && (
        <View style={styles.statusBar}>
          <View style={styles.statusInfo}>
            <Text style={styles.statusText}>
              {photos.length} photo{photos.length !== 1 ? 's' : ''} ready to save
            </Text>
          </View>
        </View>
      )}

      {/* Photo Grid. Plain wrapping row + <img> on purpose: a FlatList/ScrollView
          nested in an auto-height card can collapse to zero height on web, which
          left captured photos with no visible thumbnail. */}
      {photos.length > 0 ? (
        <View style={styles.photoGrid}>
          <View style={styles.photoGridRow}>
              {photos.map((item) => {
                // Defensive check for valid photo data
                if (!item || !item.data) {
                  console.warn("Invalid photo item:", item);
                  return null;
                }

                // Check if item is a video
                const isVideo = item.type === 'video' || item.name?.includes('.webm') || item.name?.includes('video_');

                return (
                  <View key={item.id} style={styles.photoGridItem}>
                    {isVideo ? (
                      <video
                        src={item.data}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        controls
                        playsInline
                      />
                    ) : (
                      <img
                        src={item.data}
                        alt={item.name || "photo"}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        onError={() => console.error("Image render error for photo", item.id)}
                      />
                    )}
                    {/* Status indicator */}
                    {item.status === "queued" && (
                      <View style={styles.queuedBadge}>
                        <Text style={styles.queuedIcon}>⏰</Text>
                      </View>
                    )}
                    {item.status === "uploading" && (
                      <View style={styles.uploadingOverlay}>
                        <ActivityIndicator color="#FFFFFF" size="small" />
                        <Text style={styles.uploadingText}>Uploading...</Text>
                      </View>
                    )}
                    {item.status === "uploaded" && (
                      <View style={styles.uploadedBadge}>
                        <Text style={styles.uploadedCheck}>✓</Text>
                      </View>
                    )}
                    {item.status === "failed" && (
                      <View style={styles.failedBadge}>
                        <Text style={styles.failedIcon}>!</Text>
                      </View>
                    )}
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.removePhotoButton}
                      onPress={() => handleRemovePhoto(item.id)}
                    >
                      <Text style={styles.removePhotoText}>✕</Text>
                    </TouchableOpacity>
                  </View>
                );
              })}
          </View>
        </View>
      ) : savedMedia.length === 0 ? (
        <View style={styles.photoPlaceholder}>
          <Text style={styles.placeholderText}>📷</Text>
          <Text style={styles.placeholderSubtext}>
              Photos will show here
          </Text>
        </View>
      ) : null}

      {/* Already on the booking. Saving clears the staging grid above, so this is
          the only on-screen confirmation that the upload actually happened. */}
      {savedMedia.length > 0 && (
        <View style={styles.savedMediaCard}>
          <Text style={styles.savedMediaTitle}>
            ✓ {savedMedia.length} saved to this booking
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.savedMediaRow}>
              {savedMedia.map((item, index) => (
                <View key={`${item.url}-${index}`} style={styles.savedMediaItem}>
                  {item.type === "video" ? (
                    <video
                      src={item.url}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={item.url}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  )}
                </View>
              ))}
            </View>
          </ScrollView>
        </View>
      )}

      {/* Photo Message Input — the caption is only persisted by the check-in
          save path, so the box only shows there */}
      {fromCheckIn && (
        <TextInput
          style={styles.photoMessageInput}
          placeholder={recordedVideo ? "Write a message for your video..." : "Attach message or caption..."}
          placeholderTextColor="#8A9794"
          value={photoMessage}
          onChangeText={setPhotoMessage}
          multiline
          maxLength={500}
        />
      )}

      {/* Video Recording Indicator */}
      {isRecording && (
        <View style={styles.recordingIndicator}>
          <View style={styles.recordingDot} />
          <Text style={styles.recordingText}>
            Recording: {recordingTime}s / 30s
          </Text>
        </View>
      )}

      {/* Camera Button */}
      <TouchableOpacity dataSet={{ hover: "lift" }}
        style={[
          styles.cameraButton,
          ((!currentCustomer && !fromCheckIn) || hasVideo()) && styles.cameraButtonDisabled,
        ]}
        onPress={handleTakePhoto}
        disabled={(!currentCustomer && !fromCheckIn) || hasVideo()}
      >
        <Text style={styles.cameraButtonIcon}>📸</Text>
        <Text style={styles.cameraButtonText}>
          {(currentCustomer || fromCheckIn) ? (hasVideo() ? "Video Ready - Camera Disabled" : "Open Camera") : "Select Customer First"}
        </Text>
      </TouchableOpacity>

      {/* Action Buttons Container */}
      <View style={styles.actionButtonsContainer}>
        {/* The only action in check-out mode: saves whatever is staged and
            returns to the booking. */}
        {isCheckOut && (() => {
          const busy = uploadingPhotos || completingWithoutPhotos;
          const stagedCount = photos.length;
          const canComplete =
            !busy && (hasStagedMedia || savedMedia.length > 0 || completeUnlockedByOverride);

          const hint = busy
            ? "Working..."
            : hasStagedMedia
            ? `Saves ${stagedCount || 1} item${stagedCount > 1 ? "s" : ""} and returns to the booking`
            : savedMedia.length > 0
            ? "Returns to the booking"
            : completeUnlockedByOverride
            ? "Closes out this check-out with no photos"
            : "Take a picture first";

          return (
            <TouchableOpacity dataSet={canComplete ? { hover: "lift" } : undefined}
              style={[styles.completeCheckOutButton, !canComplete && styles.actionButtonDisabled]}
              onPress={handleCompleteCheckOut}
              disabled={!canComplete}
            >
              <Text style={[styles.completeCheckOutText, !canComplete && styles.actionButtonTextDisabled]}>
                {busy ? "Completing..." : "✓ Complete Check-Out"}
              </Text>
              <Text style={[styles.completeCheckOutHint, !canComplete && styles.actionButtonTextDisabled]}>
                {hint}
              </Text>
            </TouchableOpacity>
          );
        })()}

        {/* Check-out mode has no Save button — Complete Check-Out above does the
            identical save-and-return, so a second button would just be a twin. */}
        {!isCheckOut && (() => {
          const saveBlocked =
            (photos.length === 0 && !recordedVideo) ||
            uploadingPhotos ||
            fromMessages;

          return (
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.saveButton, saveBlocked && styles.actionButtonDisabled]}
              onPress={async () => {
                // The blocking modal veils the page while handleSave uploads;
                // a successful save returns to the page we came from.
                const saved = await handleSave();
                if (saved) navigate(-1);
              }}
              disabled={saveBlocked}
            >
              <Text style={[styles.saveButtonText, saveBlocked && styles.actionButtonTextDisabled]}>
                {uploadingPhotos ? "Saving..." : "Save"}
              </Text>
            </TouchableOpacity>
          );
        })()}

        {/* Admin override — unlocks Complete Check-Out so it can close with no photos */}
        {canOverrideCheckOut && (
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[
              styles.overrideButton,
              overrideArmed && styles.overrideButtonArmed,
              completingWithoutPhotos && styles.actionButtonDisabled,
            ]}
            onPress={() => setOverrideArmed((v) => !v)}
            disabled={completingWithoutPhotos}
          >
            <Text style={styles.overrideButtonText}>
              {overrideArmed
                ? "🔓 Override On — Complete Check-Out enabled"
                : "🔒 Admin Override: Complete Without Photos"}
            </Text>
            <Text style={styles.overrideButtonHint}>
              {overrideArmed
                ? "Tap Complete Check-Out to close it out with no photos. Tap here to cancel."
                : `Auth level ${ADMIN_BADGE_AUTH_LEVEL}+ only`}
            </Text>
          </TouchableOpacity>
        )}
      </View>

    </>
  );

  return (
    <View style={[styles.container, isDesktop && styles.desktopContainer]}>
      {/* One back control for the page: round, floating, bottom-left. */}
      <FloatingBackButton
        style={isDesktop && styles.floatingBackButtonDesktop}
        onPress={() => {
          if (fromProblemReport && returnTo) {
            // Return photos to problem report modal (only non-video photos)
            const photoData = photos
              .filter(photo => !(photo.type === 'video' || photo.name?.includes('.webm')))
              .map(photo => photo.data || photo.uri || photo);
            if (photoData.length > 0) {
              navigate(returnTo, {
                state: {
                  returnedFromPhoto: true,
                  photos: photoData,
                },
              });
            } else {
              // No photos to return, just navigate back
              navigate(returnTo);
            }
            // Don't clear photos here - they're being returned
          } else {
            clearUnsentMedia();
            navigate(-1);
          }
        }}
      />

      {/* Scrolls on purpose: the page is a fixed 100vh with overflow hidden, so on
          a phone the Save / Complete buttons sat below the fold with no way to
          reach them once a photo pushed the layout down. */}
      {(() => {
        // Extra bottom padding while the floating counter is up, so it can never
        // park itself on top of the Complete Check-Out button.
        const scrollContentStyle = [
          styles.pageScrollContent,
          (uploadedCount > 0 || photos.length > 0) && styles.pageScrollContentWithCounter,
        ];
        const scroller = (
          <ScrollView style={styles.pageScroll} contentContainerStyle={scrollContentStyle}>
            {content}
          </ScrollView>
        );
        return isDesktop ? (
          <View style={styles.desktopWrapper}>{scroller}</View>
        ) : (
          scroller
        );
      })()}

      {overlays}

      {/* Progress veil while Save uploads the files */}
      <Modal
        visible={uploadingPhotos}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {}} // Prevent closing while processing
      >
        <View style={styles.blockingModalOverlay}>
          <View style={styles.blockingModalContainer}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.blockingModalText}>Uploading:</Text>
            {(() => {
              // The save path uploads every non-video item in `photos`, plus at
              // most ONE video (recordedVideo also sits in `photos`, so count
              // presence, not occurrences).
              const pictureCount = photos.filter(
                (item) => !(item.type === 'video' || item.name?.includes('.webm'))
              ).length;
              const videoCount =
                recordedVideo || photos.some((item) => item.type === 'video' || item.name?.includes('.webm'))
                  ? 1
                  : 0;
              return (
                <>
                  {pictureCount > 0 && (
                    <Text style={styles.blockingModalDetail}>
                      {pictureCount} picture{pictureCount !== 1 ? "s" : ""}
                    </Text>
                  )}
                  {videoCount > 0 && (
                    <Text style={styles.blockingModalDetail}>
                      {videoCount} video{videoCount !== 1 ? "s" : ""}
                    </Text>
                  )}
                </>
              );
            })()}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 20,
    paddingVertical: 40,
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
  },
  desktopContainer: {
    backgroundColor: "#FFFFFF",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
    height: "100vh",
    overflow: "hidden",
    position: "relative",
    paddingHorizontal: 16,
  },
  pageScroll: {
    width: "100%",
    flex: 1,
  },
  pageScrollContent: {
    alignItems: "center",
    // Enough tail room that the floating back button (50px + 20px inset) can
    // never sit on top of the last action button at end of scroll.
    paddingBottom: 90,
  },
  pageScrollContentWithCounter: {
    paddingBottom: 150,
  },
  offlineBanner: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: "#B4761B",
    paddingVertical: 10,
    paddingHorizontal: 20,
    zIndex: 1000,
  },
  offlineBannerText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
    textAlign: "center",
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 15,
  },
  customerBadge: {
    backgroundColor: "#E3F2EF",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    marginBottom: 15,
    alignItems: "center",
    alignSelf: "center",
    width: "100%",
    maxWidth: 500,
  },
  customerBadgeLabel: {
    fontSize: 12,
    color: "#5C6B68",
    marginBottom: 4,
  },
  customerBadgeName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#0E7C6B",
  },
  checkInBadge: {
    backgroundColor: "#E4F4EC",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    marginBottom: 15,
    alignSelf: "center",
    width: "100%",
    maxWidth: 500,
  },
  checkInBadgeLabel: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1F8A5B",
    marginBottom: 4,
  },
  checkInBadgeText: {
    fontSize: 14,
    color: "#1F8A5B",
  },
  noCustomerBadge: {
    backgroundColor: "#FDF3E3",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    marginBottom: 15,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
    alignSelf: "center",
    width: "100%",
    maxWidth: 500,
  },
  noCustomerText: {
    fontSize: 14,
    color: "#7A4E12",
    fontWeight: "600",
  },
  subtitle: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 20,
    textAlign: "center",
  },
  devInfo: {
    position: "absolute",
    bottom: 100,
    left: 20,
    right: 20,
    backgroundColor: "#FDF3E3",
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  devInfoText: {
    fontSize: 13,
    color: "#7A4E12",
    textAlign: "center",
    fontWeight: "600",
  },
  statusBar: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 8,
    marginBottom: 15,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    alignSelf: "center",
  },
  statusInfo: {
    flexDirection: "row",
    alignItems: "center",
  },
  statusText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#0E7C6B",
  },
  statusSpinner: {
    marginLeft: 10,
  },
  statusComplete: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1F8A5B",
  },
  photoPlaceholder: {
    width: "100%",
    maxWidth: 500,
    height: 250,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
    marginVertical: 20,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    alignSelf: "center",
  },
  photoGrid: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 12,
    marginBottom: 20,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    alignSelf: "center",
  },
  photoGridItem: {
    width: "31%",
    aspectRatio: 1,
    margin: "1%",
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#EDF1F0",
  },
  gridImage: {
    width: "100%",
    height: "100%",
  },
  gridVideo: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  photoGridRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
  },
  // Confirmation strip for media that is already on the booking.
  savedMediaCard: {
    width: "100%",
    maxWidth: 500,
    alignSelf: "center",
    backgroundColor: "#E4F4EC",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#1F8A5B",
    padding: 12,
    marginBottom: 15,
  },
  savedMediaTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#1F8A5B",
    marginBottom: 8,
  },
  savedMediaRow: {
    flexDirection: "row",
    gap: 8,
  },
  savedMediaItem: {
    width: 64,
    height: 64,
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#EDF1F0",
  },
  // Filmstrip inside the camera so a shot is visible the moment it is taken.
  cameraFilmstrip: {
    backgroundColor: "#0F1A19",
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  cameraFilmstripLabel: {
    color: "#EDF1F0",
    fontSize: 11,
    fontWeight: "700",
    marginBottom: 6,
  },
  cameraFilmstripRow: {
    flexDirection: "row",
    gap: 8,
  },
  cameraFilmstripItem: {
    width: 56,
    height: 56,
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#2E3A38",
    position: "relative",
  },
  filmstripRemoveButton: {
    position: "absolute",
    top: 2,
    right: 2,
    backgroundColor: "rgba(196, 69, 61, 0.95)",
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },
  filmstripRemoveText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "bold",
    lineHeight: 12,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonIconDisabled: {
    opacity: 0.5,
  },
  removePhotoButton: {
    position: "absolute",
    top: 5,
    right: 5,
    backgroundColor: "rgba(255, 59, 48, 0.9)",
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  removePhotoText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    lineHeight: 18,
  },
  photoNumberBadge: {
    position: "absolute",
    bottom: 5,
    left: 5,
    backgroundColor: "rgba(0, 122, 255, 0.9)",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  photoNumberText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
  },
  queuedBadge: {
    position: "absolute",
    top: 5,
    right: 5,
    backgroundColor: "rgba(255, 152, 0, 0.95)",
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  queuedIcon: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
  },
  uploadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    alignItems: "center",
    justifyContent: "center",
  },
  uploadingText: {
    color: "#FFFFFF",
    fontSize: 10,
    marginTop: 4,
    fontWeight: "600",
  },
  uploadedBadge: {
    position: "absolute",
    top: 5,
    right: 5,
    backgroundColor: "rgba(40, 167, 69, 0.95)",
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  uploadedCheck: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  failedBadge: {
    position: "absolute",
    top: 5,
    right: 5,
    backgroundColor: "rgba(255, 59, 48, 0.95)",
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  failedIcon: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
  },
  placeholderText: {
    fontSize: 64,
    marginBottom: 10,
  },
  placeholderSubtext: {
    fontSize: 14,
    color: "#8A9794",
  },
  recordingIndicator: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#C4453D",
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 15,
    alignSelf: "center",
    width: "100%",
    maxWidth: 500,
  },
  recordingDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
    marginRight: 8,
  },
  recordingText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  videoContainer: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 8,
    marginBottom: 15,
    alignSelf: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  videoLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#0E7C6B",
  },
  removeVideoButton: {
    backgroundColor: "#C4453D",
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 5,
  },
  removeVideoText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "600",
  },
  recordVideoButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
  },
  recordVideoButtonIcon: {
    fontSize: 40,
    color: "#C4453D",
  },
  stopRecordingButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
  },
  stopRecordingButtonIcon: {
    fontSize: 40,
    color: "#2E3A38",
  },
  photoMessageInput: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    paddingHorizontal: 15,
    paddingVertical: 12,
    fontSize: 16,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    marginBottom: 15,
    alignSelf: "center",
    minHeight: 50,
    maxHeight: 120,
    textAlignVertical: "top",
  },
  cameraButton: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: "#0E7C6B",
    paddingVertical: 16,
    borderRadius: 10,
    alignItems: "center",
    alignSelf: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 5,
    marginBottom: 20,
  },
  cameraButtonDisabled: {
    backgroundColor: "#AEB8B6",
    // Flattens the base button's boxShadow. A lone shadowOpacity no longer has
    // any effect now that elevation is expressed as boxShadow.
    boxShadow: "none",
  },
  cameraButtonIcon: {
    fontSize: 32,
    marginBottom: 4,
  },
  cameraButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  actionButtonsContainer: {
    width: "100%",
    maxWidth: 500,
    flexDirection: "column",
    gap: 12,
    paddingHorizontal: 16,
    marginTop: 16,
    marginBottom: 16,
    alignSelf: "center",
  },
  // Primary action on the check-out screen -- deliberately louder than Save.
  completeCheckOutButton: {
    width: "100%",
    backgroundColor: "#0E7C6B",
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 10px 24px rgba(15, 26, 25, 0.12)",
    elevation: 6,
  },
  completeCheckOutText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
  },
  completeCheckOutHint: {
    color: "#FFFFFF",
    fontSize: 11,
    marginTop: 2,
    opacity: 0.85,
  },
  saveButton: {
    width: "100%",
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  saveButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  // Tinted rather than solid, and warning rather than danger: this is an exception
  // path, not a destructive one, and it must not compete with Save for the eye.
  overrideButton: {
    width: "100%",
    backgroundColor: colors.warningSoft,
    borderWidth: 1,
    borderColor: colors.warning,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.md,
    alignItems: "center",
    justifyContent: "center",
    boxShadow: elevation.sm,
    elevation: 2,
  },
  overrideButtonArmed: {
    backgroundColor: colors.warning,
    borderColor: colors.warning,
  },
  overrideButtonText: {
    color: colors.warningInk,
    fontSize: 15,
    fontWeight: "700",
  },
  overrideButtonHint: {
    color: colors.warningInk,
    fontSize: 11,
    marginTop: spacing.xs / 2,
    opacity: 0.8,
  },
  actionButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.5,
  },
  actionButtonTextDisabled: {
    color: "#8A9794",
  },
  webcamOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    zIndex: 2000,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  webcamContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    width: "100%",
    maxWidth: 640,
    maxHeight: "90vh",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(15, 26, 25, 0.1), 0 28px 64px rgba(15, 26, 25, 0.18)",
    elevation: 20,
    position: "relative",
  },
  webcamContainerMobile: {
    maxWidth: "100%",
    maxHeight: "100vh",
    height: "100vh",
    borderRadius: 0,
    flex: 1,
  },
  webcamHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
    backgroundColor: "#FFFFFF",
  },
  webcamTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  closeWebcamButton: {
    fontSize: 28,
    color: "#5C6B68",
    fontWeight: "bold",
    cursor: "pointer",
    paddingHorizontal: 8,
  },
  videoContainer: {
    width: "100%",
    height: 480,
    backgroundColor: "#0F1A19",
    position: "relative",
  },
  videoContainerMobile: {
    flex: 1,
    height: "auto",
  },
  cameraLoadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    gap: 16,
  },
  cameraLoadingText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    marginTop: 12,
  },
  webcamActions: {
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    paddingTop: 3,
    paddingBottom: 5,
    paddingHorizontal: 16,
  },
  backButtonCamera: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
  },
  backButtonCameraIcon: {
    fontSize: 40,
    color: "#0E7C6B",
    fontWeight: "bold",
    lineHeight: 40,
  },
  captureButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    marginTop: -10,
  },
  captureButtonIcon: {
    fontSize: 49,
    lineHeight: 49,
  },
  closeButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    flexDirection: "row",
    gap: 8,
  },
  closeButtonIcon: {
    fontSize: 53,
    fontWeight: "bold",
    lineHeight: 53,
  },
  closeButtonIconGreen: {
    color: "#1F8A5B",
  },
  closeButtonIconRed: {
    color: "#C4453D",
  },
  flipButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
  },
  flipButtonIcon: {
    fontSize: 37,
  },
  photoCounter: {
    position: "fixed",
    bottom: 20,
    right: 20,
    backgroundColor: "#0E7C6B",
    borderRadius: 12,
    padding: 16,
    minWidth: 120,
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
    zIndex: 1000,
  },
  photoCounterNumber: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#FFFFFF",
    lineHeight: 40,
  },
  photoCounterLabel: {
    fontSize: 14,
    color: "#FFFFFF",
    marginTop: 4,
    marginBottom: 8,
  },
  uploadIndicator: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "rgba(255, 255, 255, 0.3)",
    width: "100%",
    justifyContent: "center",
  },
  uploadIndicatorText: {
    fontSize: 11,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  uploadSpinner: {
    marginLeft: 4,
  },
  uploadComplete: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "bold",
    marginLeft: 4,
  },
  cameraPhotoCounter: {
    position: "absolute",
    bottom: 100,
    right: 16,
    backgroundColor: "rgba(0, 122, 255, 0.95)",
    borderRadius: 8,
    padding: 12,
    minWidth: 80,
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
  },
  cameraCounterNumber: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#FFFFFF",
    lineHeight: 32,
  },
  cameraCounterLabel: {
    fontSize: 11,
    color: "#FFFFFF",
    marginTop: 2,
  },
  cameraCounterUpload: {
    fontSize: 10,
    color: "#FFFFFF",
    marginTop: 4,
    opacity: 0.9,
  },
  cameraPendingIndicator: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
    paddingVertical: 4,
  },
  cameraPendingText: {
    fontSize: 12,
    color: "#FFFFFF",
    opacity: 0.9,
  },
  cameraSpinner: {
    marginLeft: 4,
  },
  fullScreenLoadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    gap: 20,
  },
  fullScreenLoadingText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
    marginTop: 16,
  },



  backButtonCameraIcon: {
    fontSize: 40,
    color: "#0E7C6B",
    fontWeight: "bold",
    lineHeight: 40,
  },
  captureButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    marginTop: -10,
  },
  captureButtonIcon: {
    fontSize: 49,
    lineHeight: 49,
  },
  closeButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    flexDirection: "row",
    gap: 8,
  },
  closeButtonIcon: {
    fontSize: 53,
    fontWeight: "bold",
    lineHeight: 53,
  },
  closeButtonIconGreen: {
    color: "#1F8A5B",
  },
  closeButtonIconRed: {
    color: "#C4453D",
  },
  flipButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
  },
  flipButtonIcon: {
    fontSize: 37,
  },
  photoCounter: {
    position: "fixed",
    bottom: 20,
    right: 20,
    backgroundColor: "#0E7C6B",
    borderRadius: 12,
    padding: 16,
    minWidth: 120,
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
    zIndex: 1000,
  },
  photoCounterNumber: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#FFFFFF",
    lineHeight: 40,
  },
  photoCounterLabel: {
    fontSize: 14,
    color: "#FFFFFF",
    marginTop: 4,
    marginBottom: 8,
  },
  uploadIndicator: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "rgba(255, 255, 255, 0.3)",
    width: "100%",
    justifyContent: "center",
  },
  uploadIndicatorText: {
    fontSize: 11,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  uploadSpinner: {
    marginLeft: 4,
  },
  uploadComplete: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "bold",
    marginLeft: 4,
  },
  cameraPhotoCounter: {
    position: "absolute",
    bottom: 100,
    right: 16,
    backgroundColor: "rgba(0, 122, 255, 0.95)",
    borderRadius: 8,
    padding: 12,
    minWidth: 80,
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
  },
  cameraCounterNumber: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#FFFFFF",
    lineHeight: 32,
  },
  cameraCounterLabel: {
    fontSize: 11,
    color: "#FFFFFF",
    marginTop: 2,
  },
  cameraCounterUpload: {
    fontSize: 10,
    color: "#FFFFFF",
    marginTop: 4,
    opacity: 0.9,
  },
  cameraPendingIndicator: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
    paddingVertical: 4,
  },
  cameraPendingText: {
    fontSize: 12,
    color: "#FFFFFF",
    opacity: 0.9,
  },
  cameraSpinner: {
    marginLeft: 4,
  },
  fullScreenLoadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    gap: 20,
  },
  fullScreenLoadingText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
    marginTop: 16,
  },
  blockingModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
  },
  blockingModalContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 24,
    alignItems: "center",
    minWidth: 200,
  },
  blockingModalText: {
    marginTop: 16,
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "500",
  },
  blockingModalDetail: {
    marginTop: 6,
    fontSize: 13,
    color: "#5C6B68",
  },
  // Lines the floating back button up with the centered 800px desktop column.
  floatingBackButtonDesktop: {
    left: 'calc(50% - 400px + 16px)',
  },
});

export default Photo;

